// src/pages/outsourcing_management/requisitions/RequisitionFilterBar.tsx
//
// Filter bar for the Requisition dashboard (EE-207, Phase 2 #7):
// status chips + type/purpose/material/tool/supplier selects + SO/part id
// fields + created date range. Dropdowns are code-keyed against
// useRequisitionOptions - render `label`, filter by `code` (never
// hardcode label strings, per the Phase 1 casing notes).
//
// The controls come from components/Dashboard/filterFields, shared with
// InquiryFilterBar. The id fields gained that file's debounce and 9-digit cap
// in the process, which they did not have before.
import { FunctionComponent, useRef } from "react";
import {
  RequisitionOptionsType,
  RequisitionStatusEnum,
} from "types/RequisitionTypes";
import StatusFilterChips from "components/Dashboard/StatusFilterChips";
import {
  REQUISITION_STATUS_COLORS,
  REQUISITION_STATUS_LABELS,
} from "../constants/requisitionStatusColors";
import { SupplierAutocomplete } from "components/Dashboard/supplierOption";
import {
  ClearFiltersButton,
  FilterDateField,
  FilterIdField,
  FilterRow,
  FilterSelect,
} from "components/Dashboard/filterFields";

const ALL_REQUISITION_STATUSES = Object.values(RequisitionStatusEnum);

export type RequisitionFilterState = {
  status: RequisitionStatusEnum[];
  typeCode: string;
  purposeCode: string;
  materialTypeCode: string;
  toolCategoryCode: string;
  supplierId: number | undefined;
  salesOrderId: string;
  orderItemId: string;
  quoteId: string;
  createdFrom: string;
  createdTo: string;
};

export const EMPTY_REQUISITION_FILTERS: RequisitionFilterState = {
  status: [],
  typeCode: "",
  purposeCode: "",
  materialTypeCode: "",
  toolCategoryCode: "",
  supplierId: undefined,
  salesOrderId: "",
  orderItemId: "",
  quoteId: "",
  createdFrom: "",
  createdTo: "",
};

type PropsType = {
  filters: RequisitionFilterState;
  onChange: (filters: RequisitionFilterState) => void;
  options?: RequisitionOptionsType;
};

const RequisitionFilterBar: FunctionComponent<PropsType> = ({
  filters,
  onChange,
  options,
}) => {
  // See InquiryFilterBar: a debounced id commit can land after a dropdown has
  // already patched `filters`, and merging onto the ref keeps that edit.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const patch = (changes: Partial<RequisitionFilterState>) =>
    onChange({ ...filtersRef.current, ...changes });

  const toggleStatus = (status: RequisitionStatusEnum) =>
    patch({
      status: filters.status.includes(status)
        ? filters.status.filter((s) => s !== status)
        : [...filters.status, status],
    });

  const toggleAllStatuses = () =>
    patch({
      status:
        filters.status.length === ALL_REQUISITION_STATUSES.length
          ? []
          : [...ALL_REQUISITION_STATUSES],
    });

  const hasActiveFilters =
    filters.status.length > 0 ||
    Object.entries(filters).some(([key, value]) => key !== "status" && !!value);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      <StatusFilterChips
        statuses={ALL_REQUISITION_STATUSES}
        colors={REQUISITION_STATUS_COLORS}
        labels={REQUISITION_STATUS_LABELS}
        selected={filters.status}
        onToggle={toggleStatus}
        onToggleAll={toggleAllStatuses}
      />
      <FilterRow>
        <FilterSelect
          label="Type"
          value={filters.typeCode}
          onChange={(typeCode) => patch({ typeCode })}
          options={options?.types}
        />
        <FilterSelect
          label="Purpose"
          value={filters.purposeCode}
          onChange={(purposeCode) => patch({ purposeCode })}
          options={options?.purposes}
        />
        <FilterSelect
          label="Material Type"
          value={filters.materialTypeCode}
          onChange={(materialTypeCode) => patch({ materialTypeCode })}
          options={options?.materialTypes}
        />
        <FilterSelect
          label="Tool Category"
          value={filters.toolCategoryCode}
          onChange={(toolCategoryCode) => patch({ toolCategoryCode })}
          options={options?.toolCategories}
        />
        <SupplierAutocomplete
          value={filters.supplierId}
          onChange={(supplierId) => patch({ supplierId })}
          sx={{ minWidth: 220, background: "var(--white)" }}
        />
        <FilterIdField
          label="Sales Order ID"
          value={filters.salesOrderId}
          onChange={(salesOrderId) => patch({ salesOrderId })}
        />
        <FilterIdField
          label="Part ID"
          value={filters.orderItemId}
          onChange={(orderItemId) => patch({ orderItemId })}
          minWidth={120}
        />
        <FilterIdField
          label="Quote ID"
          value={filters.quoteId}
          onChange={(quoteId) => patch({ quoteId })}
          minWidth={120}
        />
        <FilterDateField
          label="Created From"
          value={filters.createdFrom}
          onChange={(createdFrom) => patch({ createdFrom })}
        />
        <FilterDateField
          label="Created To"
          value={filters.createdTo}
          onChange={(createdTo) => patch({ createdTo })}
        />
        <ClearFiltersButton
          disabled={!hasActiveFilters}
          onClick={() => onChange({ ...EMPTY_REQUISITION_FILTERS })}
        />
      </FilterRow>
    </div>
  );
};

export default RequisitionFilterBar;
